import React from 'react';

import ItemsSelectionBar from './ItemsSelectionBar';
import ItemsMoveModal from './ItemsMoveModal';
import ItemsTagsPanel from './ItemsTagsPanel';
import BulkAttachPanel from './BulkAttachPanel';
import ItemsApplyPanel from './ItemsApplyPanel';
import { parentOf } from '../../utils/collectionFamily';
import { getItemDisplayValue } from '../../utils/items';
import { normalizeId } from '../../utils/ids';

/**
 * The items page's five bulk surfaces (KPR-1566, moved out of
 * CollectionItemsPage as they stood): the selection bar, and the Move, Tags,
 * Attach files and apply panels it opens. All their state is
 * hooks/useItemsBulkState's, handed in as `bulk`; the rest is the page's own
 * context. A fragment, so the page's DOM is what it was.
 *
 * Imports no service itself — the attach panel's after-each-file read is the
 * hook's `handleAttached`, and the Move cap (services/subCollections) is read
 * there too, as `moveDisabled` / `moveReason` — so it is no data-fetching
 * surface to the loading-state gate.
 */
export default function ItemsBulkSurfaces({
	bulk,
	isMobile,
	totalCount,
	linkedOn,
	cardFilter,
	feedQuery,
	review,
	subCollections,
	collection,
	collectionId,
	cardsById,
	getElementsForCard,
	resolveElements,
	resolveLookupValue,
	refreshItems,
}) {
	const {
		selection,
		selectionEnabled,
		selectableIds,
		selectedItems,
		bulkCardId,
		moveDisabled,
		moveReason,
		changeCard,
		selectionFilter,
		applyMode,
		setApplyMode,
		moveOpen,
		setMoveOpen,
		tagsOpen,
		setTagsOpen,
		attachScope,
		attachRequest,
		openAttachForSelection,
		closeAttach,
		handleAttached,
		takesFiles,
		titleForItem,
		canTakeFiles,
		handleApplied,
	} = bulk;

	return (
		<>
			{selectionEnabled && selection.active ? (
				<ItemsSelectionBar
					variant={isMobile ? 'dock' : 'bar'}
					count={selection.count}
					allMatching={selection.allMatching}
					totalCount={totalCount}
					loadedCount={selectableIds.length}
					// "All N matching" is a by-filter apply, and the filter it
					// means is the one that went to the server. Offered only
					// when there IS a filter to name — "everything in the
					// collection" is not a selection, it is a different feature.
					// …and never over a merged list: the filter names records of
					// other collections, which no apply here may touch.
					canOfferAllMatching={!linkedOn && Boolean(cardFilter || feedQuery || review)}
					onSelectAllMatching={selection.selectAllMatching}
					onSelectVisible={() => selection.selectVisible(selectableIds)}
					onClear={selection.clear}
					onSetValue={() => setApplyMode('set')}
					onTags={linkedOn ? null : () => setTagsOpen(true)}
					onAttach={takesFiles ? openAttachForSelection : null}
					onDelete={() => setApplyMode('delete')}
					// Triage is offered where submissions waiting for review
					// can be in the selection: the queue, and "any".
					onAccept={review === 'pending' || review === 'any' ? () => setApplyMode('accept') : null}
					onReject={review === 'pending' || review === 'any' ? () => setApplyMode('reject') : null}
					// Sub-collections: Move to… another collection of the family,
					// by the selected ids (never a filter-wide move), 100 at a time.
					onChangeCard={changeCard.go}
					changeCardLabel={changeCard.label}
					changeCardDisabled={changeCard.disabled}
					changeCardReason={changeCard.reason}
					onMove={subCollections.length || parentOf(collection) ? () => setMoveOpen(true) : null}
					moveDisabled={moveDisabled}
					moveReason={moveReason}
					setValueDisabled={!bulkCardId}
					setValueReason={bulkCardId ? null : 'These items use more than one card type. Filter by card type to set a field.'}
				/>
			) : null}

			{moveOpen ? (
				<ItemsMoveModal
					open={moveOpen}
					onClose={() => setMoveOpen(false)}
					collection={collection}
					items={selectedItems}
					ids={selection.ids}
					titleOf={item => getItemDisplayValue({
						card: cardsById?.[normalizeId(item?.card_id)],
						elementDefs: resolveElements(item?.card_id),
						item,
						resolveLookupValue,
					})}
					onMoved={() => {
						selection.clear();
						refreshItems();
					}}
				/>
			) : null}

			<ItemsTagsPanel
				open={tagsOpen}
				onClose={() => setTagsOpen(false)}
				collectionId={collectionId}
				collection={collection}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
				items={selectedItems}
				ids={selection.ids}
				allMatching={selection.allMatching}
				matchingCount={selection.matchingCount}
				excluded={selection.excluded}
				filter={selectionFilter}
				resolveLookupValue={resolveLookupValue}
				onApplied={handleApplied}
				onDropIds={ids => selection.forget(ids)}
			/>

			<BulkAttachPanel
				open={Boolean(attachScope)}
				scope={attachScope}
				onClose={closeAttach}
				onAttached={handleAttached}
				collection={collection}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
				titleFor={titleForItem}
				canTakeFiles={canTakeFiles}
				request={attachRequest}
			/>

			<ItemsApplyPanel
				open={Boolean(applyMode)}
				mode={applyMode}
				onClose={() => setApplyMode(null)}
				collectionId={collectionId}
				collection={collection}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
				items={selectedItems}
				ids={selection.ids}
				allMatching={selection.allMatching}
				matchingCount={selection.matchingCount}
				excluded={selection.excluded}
				filter={selectionFilter}
				resolveLookupValue={resolveLookupValue}
				onApplied={handleApplied}
				onDropIds={ids => selection.forget(ids)}
			/>
		</>
	);
}
